/*
| Entry point.
|
| A static single-page app over Monaco's public REST API. The shell is
| index.html; every screen renders into #view from the API. Navigation uses
| real URLs through the History API, and the Worker serves the shell for every
| route with that route's meta tags, so a pasted link previews correctly.
*/

import './css/app.css';

import { createApi } from './lib/api.js';
import { readConfig } from './lib/config.js';
import { esc } from './lib/format.js';
import { createLinks } from './lib/links.js';
import { mountSearch } from './lib/search.js';
import { createRetryPolicy } from './lib/retry.js';
import { shouldReloadForStaleChunk } from './lib/stale-chunk.js';
import { mountTableBehaviour } from './lib/tables.js';
import { screens } from './screens/index.js';

const config = readConfig(import.meta.env);
const links = createLinks(config);

function sessionStore() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

const api = createApi({ baseUrl: config.apiUrl, storage: sessionStore() });

const view = document.getElementById('view');
document.getElementById('network').textContent = `${config.networkName} · ${config.networkChain}`;

/** The bare root is the overview. */
const REDIRECTS = { '': '/overview' };

/** '/markets/BTC-USDC' -> { screen: 'markets', arg: 'BTC-USDC' } */
function routeFromPath(pathname) {
  const [, screen = '', ...rest] = pathname.split('/');
  let arg = rest.filter(Boolean).join('/');
  try {
    arg = decodeURIComponent(arg);
  } catch {
    // A malformed escape stays as typed; the screen answers not-found.
  }
  return { screen, arg };
}

function setActiveNav(screen) {
  document.querySelectorAll('#nav a').forEach((a) => {
    a.classList.toggle('active', a.dataset.route === screen);
  });
}

let renderToken = 0;
let retryTimer = null;
const retryPolicy = createRetryPolicy();

function errorView(error, arg, screen, willRetry) {
  if (error?.isNotFound) {
    return `<div class="pagehead"><div><h1>Not found</h1><p>Nothing here matches <span class="mono">${esc(arg || screen)}</span>.</p></div></div>`;
  }

  if (error?.isRateLimited) {
    const next = willRetry
      ? `This page reloads in ${esc(String(error.retryAfter))}s.`
      : `Try again in ${esc(String(error.retryAfter))}s.`;
    return `<div class="pagehead"><div><h1>Taking a breather</h1><p class="muted">Monaco's public read limit for this connection was reached. ${next}</p></div></div>`;
  }

  return `<div class="pagehead"><div><h1>Something went wrong</h1><p class="muted">${esc(error?.message ?? 'The API did not respond.')}</p></div></div>`;
}

/** `options.isRetry` marks the one automatic retry; anything else (a popstate event, a click) is a navigation. */
export async function render(options) {
  clearTimeout(retryTimer);
  retryPolicy.startRender({ isRetry: options?.isRetry === true });

  const { screen, arg } = routeFromPath(window.location.pathname);
  if (REDIRECTS[screen] !== undefined && !arg) {
    window.history.replaceState({}, '', REDIRECTS[screen]);
    return render(options);
  }

  const run = ++renderToken;
  setActiveNav(screen);

  const mount = screens[screen] ?? screens.notFound;
  view.innerHTML = '<div class="note">Loading…</div>';

  try {
    const html = await mount({ arg, render, api, config, links });
    if (run !== renderToken) return;
    if (typeof html === 'string') view.innerHTML = html;
  } catch (error) {
    if (run !== renderToken) return;

    // esc() on everything echoed: `arg` comes straight from the URL and the
    // message can carry upstream text.
    const willRetry = error?.isRateLimited === true && retryPolicy.takeRetry();
    view.innerHTML = errorView(error, arg, screen, willRetry);

    if (willRetry) {
      // One scheduled retry per navigation, at the server's horizon.
      retryTimer = setTimeout(() => {
        if (run === renderToken) render({ isRetry: true });
      }, Math.max(1, error.retryAfter) * 1000);
    } else if (!error?.isRateLimited && !error?.isNotFound) {
      console.error(error);
    }
  }

  window.scrollTo(0, 0);
}

/* Internal links navigate client-side; the URL stays real. */
document.addEventListener('click', (event) => {
  // Copy controls sit inside rows that navigate; the copy wins.
  const copy = event.target.closest('[data-copy]');
  if (copy) {
    event.preventDefault();
    navigator.clipboard?.writeText(copy.dataset.copy);
    copy.textContent = '✓';
    setTimeout(() => { copy.textContent = '⧉'; }, 1000);
    return;
  }

  const link = event.target.closest('a[href^="/"]');
  if (link && !link.target && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    event.preventDefault();
    if (link.getAttribute('href') !== window.location.pathname) {
      window.history.pushState({}, '', link.getAttribute('href'));
      render();
    }
    return;
  }

  // Rows and chips opt in with data-go="/markets/BTC-USDC"; a link inside a row wins.
  const go = event.target.closest('[data-go]');
  if (go && !event.target.closest('a')) {
    window.history.pushState({}, '', go.dataset.go);
    render();
  }
});

/* A broken icon URL degrades to nothing rather than a broken-image glyph. */
document.addEventListener('error', (event) => {
  if (event.target instanceof HTMLImageElement) event.target.style.display = 'none';
}, true);

/*
| A tab opened before a deploy asks for chunks the new build no longer has.
| Reload once onto the new build; the guard stops a real outage from looping.
*/
window.addEventListener('vite:preloadError', (event) => {
  if (shouldReloadForStaleChunk(sessionStore())) {
    event.preventDefault();
    window.location.reload();
  }
});

window.addEventListener('popstate', render);

mountTableBehaviour();
mountSearch({ api, links, render });
render();
